import { Button, Group, Modal, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import {
  type ReferringAgent,
  useCreateReferringAgent,
  useUpdateReferringAgent,
} from '@/models/referringAgents';

export default function ReferringAgentModal({
  opened,
  onClose,
  agent,
}: {
  opened: boolean;
  onClose: () => void;
  agent?: ReferringAgent;
}) {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={agent ? 'Edit referring agent' : 'New referring agent'}
    >
      <AgentForm key={agent?.id ?? 'new'} agent={agent} onClose={onClose} />
    </Modal>
  );
}

function AgentForm({
  agent,
  onClose,
}: {
  agent?: ReferringAgent;
  onClose: () => void;
}) {
  const create = useCreateReferringAgent();
  const update = useUpdateReferringAgent();
  const form = useForm({
    initialValues: { name: agent?.name ?? '' },
    validate: {
      name: (v) => (!v.trim() ? 'Name is required' : null),
    },
  });

  const submit = form.onSubmit(async (values) => {
    const name = values.name.trim();
    try {
      if (agent) {
        await update.mutateAsync({ id: agent.id, name });
      } else {
        await create.mutateAsync({ name });
      }
      onClose();
    } catch (e) {
      form.setFieldError('name', String(e));
    }
  });

  return (
    <form onSubmit={submit}>
      <Stack>
        <TextInput
          label="Name"
          placeholder="Agent name"
          data-autofocus
          {...form.getInputProps('name')}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={create.isPending || update.isPending}>
            {agent ? 'Save' : 'Create'}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
